// frontend/src/components/dashboard/ProductDialog.js
import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../../utils/axios';

const ProductDialog = ({ open, onClose, product, onSave }) => {
    const { t } = useTranslation();
    const [specialists, setSpecialists] = useState([]);
    const [formData, setFormData] = useState({
        name: '',
        category: '',
        price: '0',
        costPrice: '0',
        warehouse: {
            name: '',
            address: ''
        },
        articleNumber: '',
        unit: 'piece',
        dimensions: {
            weight: '0',
            length: '0',
            width: '0',
            height: '0'
        },
        stock: '0',
        responsibleEmployee: ''
    });

    useEffect(() => {
        fetchSpecialists();
    }, []);

    useEffect(() => {
        if (product) {
            setFormData({
                ...product,
                responsibleEmployee: product.responsibleEmployee?._id || ''
            });
        } else {
            resetForm();
        }
    }, [product]);

    const fetchSpecialists = async () => {
        try {
            const response = await axiosInstance.get('/specialists');
            setSpecialists(response.data.filter(s => s.role !== 'superadmin'));
        } catch (error) {
            console.error('Error fetching specialists:', error);
        }
    };

    const resetForm = () => {
        setFormData({
            name: '',
            category: '',
            price: '0',
            costPrice: '0',
            warehouse: {
                name: '',
                address: ''
            },
            articleNumber: '',
            unit: 'piece',
            dimensions: {
                weight: '0',
                length: '0',
                width: '0',
                height: '0'
            },
            stock: '0',
            responsibleEmployee: ''
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const dataToSend = {
                ...formData,
                articleNumber: formData.articleNumber || '',
                category: formData.category || '',
                price: parseFloat(formData.price) || 0,
                costPrice: parseFloat(formData.costPrice) || 0,
                stock: parseInt(formData.stock) || 0,
                responsibleEmployee: formData.responsibleEmployee || null,
                dimensions: {
                    weight: parseFloat(formData.dimensions?.weight) || 0,
                    length: parseFloat(formData.dimensions?.length) || 0,
                    width: parseFloat(formData.dimensions?.width) || 0,
                    height: parseFloat(formData.dimensions?.height) || 0
                },
                warehouse: {
                    name: formData.warehouse?.name || '',
                    address: formData.warehouse?.address || ''
                },
                unit: formData.unit || 'piece',
            };
    
            console.log('Sending data:', dataToSend);
    
            if (product) {
                await axiosInstance.patch(`/products/${product._id}`, dataToSend);
            } else {
                const response = await axiosInstance.post('/products', dataToSend);
                console.log('Response:', response);
            }
            onSave();
            onClose();
        } catch (error) {
            console.error('Error details:', error.response?.data);
        }
    };

    return (
        <Dialog 
    open={open} 
    onClose={onClose} 
    maxWidth="md" 
    fullWidth
    disableAutoFocus
    TransitionProps={{
        onExited: () => {
            resetForm();
        }
    }}
>
            <DialogTitle>
                {product ? t('products.edit') : t('products.create')}
            </DialogTitle>
            <form onSubmit={handleSubmit}>
                <DialogContent>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label={t('products.name')}
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label={t('products.category')}
                                value={formData.category}
                                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label={t('products.price')}
                                type="number"
                                value={formData.price}
                                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label={t('products.costPrice')}
                                type="number"
                                value={formData.costPrice}
                                onChange={(e) => setFormData({ ...formData, costPrice: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label={t('products.warehouseName')}
                                value={formData.warehouse.name}
                                onChange={(e) => setFormData({
                                    ...formData,
                                    warehouse: { ...formData.warehouse, name: e.target.value }
                                })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label={t('products.warehouseAddress')}
                                value={formData.warehouse.address}
                                onChange={(e) => setFormData({
                                    ...formData,
                                    warehouse: { ...formData.warehouse, address: e.target.value }
                                })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label={t('products.articleNumber')}
                                value={formData.articleNumber}
                                onChange={(e) => setFormData({ ...formData, articleNumber: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>{t('products.unit')}</InputLabel>
                                <Select
                                    value={formData.unit}
                                    onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                                    label={t('products.unit')}
                                >
                                    <MenuItem value="piece">{t('products.units.piece')}</MenuItem>
                                    <MenuItem value="kg">{t('products.units.kg')}</MenuItem>
                                    <MenuItem value="package">{t('products.units.package')}</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label={t('products.weight')}
                                type="number"
                                value={formData.dimensions.weight}
                                onChange={(e) => setFormData({
                                    ...formData,
                                    dimensions: { ...formData.dimensions, weight: e.target.value }
                                })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Grid container spacing={1}>
                                <Grid item xs={4}>
                                    <TextField
                                        fullWidth
                                        label={t('products.length')}
                                        type="number"
                                        value={formData.dimensions.length}
                                        onChange={(e) => setFormData({
                                            ...formData,
                                            dimensions: { ...formData.dimensions, length: e.target.value }
                                        })}
                                    />
                                </Grid>
                                <Grid item xs={4}>
                                    <TextField
                                        fullWidth
                                        label={t('products.width')}
                                        type="number"
                                        value={formData.dimensions.width}
                                        onChange={(e) => setFormData({
                                            ...formData,
                                            dimensions: { ...formData.dimensions, width: e.target.value }
                                        })}
                                    />
                                </Grid>
                                <Grid item xs={4}>
                                    <TextField
                                        fullWidth
                                        label={t('products.height')}
                                        type="number"
                                        value={formData.dimensions.height}
                                        onChange={(e) => setFormData({
                                            ...formData,
                                            dimensions: { ...formData.dimensions, height: e.target.value }
                                        })}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label={t('products.stock')}
                                type="number"
                                value={formData.stock}
                                onChange={(e) => setFormData({ ...formData, stock: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>{t('products.responsibleEmployee')}</InputLabel>
                                <Select
                                    value={formData.responsibleEmployee || ''}
                                    onChange={(e) => setFormData({ ...formData, responsibleEmployee: e.target.value })}
                                    label={t('products.responsibleEmployee')}
                                >
                                    <MenuItem value="">
                                        <em>{t('common.none')}</em>
                                    </MenuItem>
                                    {specialists.map((specialist) => (
                                        <MenuItem key={specialist._id} value={specialist._id}>
                                            {specialist.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                <Button onClick={onClose} disableAutoFocus>{t('common.cancel')}</Button>
                <Button type="submit" variant="contained" disableAutoFocus>
                        {product ? t('common.save') : t('common.create')}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default ProductDialog;